import { useCallback, useMemo } from 'react';

import { QueryClient, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/react-query/constants';
import useArtworkService from '@/shared/api/artwork.service';
import useFiles from '@/shared/api/files.service';
import useResourceDownloads from '@/shared/api/useResourceDownloads';
import {
  CACHE_TIME_MS,
  IMAGE_TIER_SIZE,
  ImageSizeSpec,
  ImageTier,
  ResizeFit,
  ResizeFormat,
  UseImageResult,
  getOrCreateBlobUrl,
  useCachedImage,
} from '@/shared/lib/hooks/useCachedImage';

// Re-export everything consumers need so they import from one place.
export type { ImageSizeSpec, ImageTier, ResizeFit, ResizeFormat, UseImageResult };
export { IMAGE_TIER_SIZE, getOrCreateBlobUrl };

// ---------------------------------------------------------------------------
// ImageResource registry
// ---------------------------------------------------------------------------

/**
 * Every server-fetched image flows through one of these kinds. To add a new
 * endpoint: extend the union, handle it in `resourceQueryKeyPrefix`,
 * `resourceLeafId`, and the `useResourceFetcher` switch — TypeScript's
 * exhaustiveness check will catch all three sites.
 */
export type ImageResource =
  | { kind: 'user-avatar'; userId: string }
  | { kind: 'current-user-avatar' }
  | { kind: 'thumbnail'; documentId: string }
  | { kind: 'support-name-picture'; url: string }
  | { kind: 'service-task-document'; taskId: string; documentId: string }
  | { kind: 'service-task-artwork'; taskId: string; documentId: string }
  | { kind: 'service-task-artwork-preview'; taskId: string; documentId: string }
  | { kind: 'service-task-campaign-monitor'; taskId: string; documentId: string }
  | { kind: 'service-task-artwork-match'; taskId: string; documentId: string }
  | { kind: 'service-task-layout-sketch'; taskId: string; documentId: string }
  | { kind: 'service-task-layout-sketch-original'; taskId: string; documentId: string }
  | { kind: 'space-document'; spaceId: string; documentId: string }
  | { kind: 'space-thumbnail'; spaceId: string; documentId: string }
  | { kind: 'space-support-picture'; spaceId: string; documentId: string }
  | { kind: 'space-layout-sketch'; spaceId: string; documentId: string }
  | { kind: 'location-document'; locationId: string; documentId: string }
  | { kind: 'location-floor-map'; locationId: string; documentId: string }
  | { kind: 'kiosk-picture'; kioskId: string; documentId: string }
  | { kind: 'kiosk-rental-contract'; kioskId: string; rentalId: string; documentId: string }
  | { kind: 'kiosk-rental-authorization'; kioskId: string; rentalId: string; documentId: string }
  | { kind: 'order-document'; orderId: string; documentId: string }
  | { kind: 'order-artwork'; orderId: string; bookingId: string; documentId: string }
  | { kind: 'order-artwork-preview'; orderId: string; bookingId: string; documentId: string }
  | { kind: 'order-layout-sketch-image'; orderId: string; documentId: string }
  | { kind: 'order-booking-artwork-match'; orderId: string; bookingId: string; documentId: string }
  | {
      kind: 'order-booking-campaign-monitor';
      orderId: string;
      bookingId: string;
      documentId: string;
    }
  | { kind: 'mediaplan-artwork'; planId: string; bookingId: string; documentId: string }
  | { kind: 'mediaplan-artwork-preview'; planId: string; bookingId: string; documentId: string }
  | { kind: 'mediaplan-document'; planId: string; documentId: string }
  | {
      kind: 'mediaplan-booking-artwork-match';
      planId: string;
      bookingId: string;
      documentId: string;
    }
  | { kind: 'advertising-package-document'; packageId: string; documentId: string }
  | { kind: 'advertising-package-support-picture'; packageId: string; documentId: string }
  | { kind: 'company-document'; companyId: string; documentId: string }
  | { kind: 'organization-document'; organizationId: string; documentId: string };

const assertNever = (x: never): never => {
  throw new Error(`Unhandled ImageResource kind: ${JSON.stringify(x)}`);
};

/** The unique leaf identifier for this resource (becomes the last segment of the cache key). */
export const resourceLeafId = (resource: ImageResource): string => {
  switch (resource.kind) {
    case 'user-avatar':
      return resource.userId;
    case 'current-user-avatar':
      return 'me';
    case 'support-name-picture':
      return resource.url;
    case 'thumbnail':
    case 'service-task-document':
    case 'service-task-artwork':
    case 'service-task-artwork-preview':
    case 'service-task-campaign-monitor':
    case 'service-task-artwork-match':
    case 'service-task-layout-sketch':
    case 'service-task-layout-sketch-original':
    case 'space-document':
    case 'space-thumbnail':
    case 'space-support-picture':
    case 'space-layout-sketch':
    case 'location-document':
    case 'location-floor-map':
    case 'kiosk-picture':
    case 'kiosk-rental-contract':
    case 'kiosk-rental-authorization':
    case 'order-document':
    case 'order-artwork':
    case 'order-artwork-preview':
    case 'order-layout-sketch-image':
    case 'order-booking-artwork-match':
    case 'order-booking-campaign-monitor':
    case 'mediaplan-artwork':
    case 'mediaplan-artwork-preview':
    case 'mediaplan-document':
    case 'mediaplan-booking-artwork-match':
    case 'advertising-package-document':
    case 'advertising-package-support-picture':
    case 'company-document':
    case 'organization-document':
      return resource.documentId;
    default:
      return assertNever(resource);
  }
};

/** Cache-key prefix for the source blob (the leaf is appended separately by useCachedImage). */
export const resourceQueryKeyPrefix = (resource: ImageResource): readonly unknown[] => {
  switch (resource.kind) {
    case 'user-avatar':
      return [queryKeys.userAvatar];
    case 'current-user-avatar':
      return [queryKeys.currentUserAvatar];
    case 'thumbnail':
      return [queryKeys.thumbnails];
    case 'support-name-picture':
      return [queryKeys.supportNamePictures];
    case 'service-task-document':
    case 'service-task-artwork':
    case 'service-task-artwork-preview':
    case 'service-task-campaign-monitor':
    case 'service-task-artwork-match':
    case 'service-task-layout-sketch':
    case 'service-task-layout-sketch-original':
      return [queryKeys.documentBlob, resource.kind, resource.taskId];
    case 'space-document':
    case 'space-thumbnail':
    case 'space-support-picture':
    case 'space-layout-sketch':
      return [queryKeys.documentBlob, resource.kind, resource.spaceId];
    case 'location-document':
    case 'location-floor-map':
      return [queryKeys.documentBlob, resource.kind, resource.locationId];
    case 'kiosk-picture':
      return [queryKeys.documentBlob, resource.kind, resource.kioskId];
    case 'kiosk-rental-contract':
    case 'kiosk-rental-authorization':
      return [queryKeys.documentBlob, resource.kind, resource.kioskId, resource.rentalId];
    case 'order-document':
    case 'order-layout-sketch-image':
      return [queryKeys.documentBlob, resource.kind, resource.orderId];
    case 'order-artwork':
    case 'order-artwork-preview':
    case 'order-booking-artwork-match':
    case 'order-booking-campaign-monitor':
      return [queryKeys.documentBlob, resource.kind, resource.orderId, resource.bookingId];
    case 'mediaplan-artwork':
    case 'mediaplan-artwork-preview':
    case 'mediaplan-booking-artwork-match':
      return [queryKeys.documentBlob, resource.kind, resource.planId, resource.bookingId];
    case 'mediaplan-document':
      return [queryKeys.documentBlob, resource.kind, resource.planId];
    case 'advertising-package-document':
    case 'advertising-package-support-picture':
      return [queryKeys.documentBlob, resource.kind, resource.packageId];
    case 'company-document':
      return [queryKeys.documentBlob, resource.kind, resource.companyId];
    case 'organization-document':
      return [queryKeys.documentBlob, resource.kind, resource.organizationId];
  }
};

/** Full cache key for a resource (prefix + leaf). */
export const resourceQueryKey = (resource: ImageResource): readonly unknown[] => [
  ...resourceQueryKeyPrefix(resource),
  resourceLeafId(resource),
];

// ---------------------------------------------------------------------------
// Resource factories — shortcuts for the most common dispatch patterns.
// ---------------------------------------------------------------------------

/** Order vs media-plan artwork (or its preview), dispatched by which parent ID is set. */
export const bookingArtworkResource = (
  context: { orderId?: string; planId?: string; bookingId: string },
  documentId: string,
  options: { preview?: boolean } = {},
): ImageResource => {
  const { preview = false } = options;
  if (context.orderId) {
    return preview
      ? {
          kind: 'order-artwork-preview',
          orderId: context.orderId,
          bookingId: context.bookingId,
          documentId,
        }
      : {
          kind: 'order-artwork',
          orderId: context.orderId,
          bookingId: context.bookingId,
          documentId,
        };
  }
  if (!context.planId) {
    throw new Error('bookingArtworkResource requires either orderId or planId');
  }
  return preview
    ? {
        kind: 'mediaplan-artwork-preview',
        planId: context.planId,
        bookingId: context.bookingId,
        documentId,
      }
    : {
        kind: 'mediaplan-artwork',
        planId: context.planId,
        bookingId: context.bookingId,
        documentId,
      };
};

// ---------------------------------------------------------------------------
// Resource → fetch dispatcher (internal)
// ---------------------------------------------------------------------------

const useResourceFetcher = () => {
  const { downloadThumbnail } = useFiles();
  const {
    downloadArtworkFileForTask,
    downloadTaskArtworkPreview,
    downloadArtworkDocumentForOrder,
    downloadArtworkPreviewDocumentForOrder,
    downloadArtworkDocumentForMediaPlan,
    downloadArtworkPreviewDocumentForMediaPlan,
  } = useArtworkService();
  const {
    downloadServiceTaskDocument,
    downloadCampaignMonitorImage,
    downloadArtworkMatchFile,
    downloadServiceTaskLayoutSketch,
    downloadServiceTaskLayoutSketchOriginal,
    downloadSpaceDocumentById,
    downloadSpaceThumbnail,
    downloadSpaceSupportPicture,
    downloadSpaceLayoutSketch,
    downloadIndividualSupportNamePicture,
    downloadOrderDocumentById,
    downloadOrderDocumentImageById,
    downloadBookingArtworkMatchFile,
    downloadBookingCampaignMonitorFile,
    downloadDocumentById: downloadLocationDocument,
    downloadFloorMaps,
    downloadKioskPicture,
    downloadRentalContract,
    downloadRentalAuthorization,
    downloadAdvertisingPackageDocument,
    downloadPackageSupportPicture,
    downloadMediaPlanDocumentById,
    downloadMediaPlanBookingArtworkMatchFile,
    downloadCompanyDocument,
    downloadOrganizationDocument,
    getUserAvatar,
    getCurrentUserAvatar,
  } = useResourceDownloads();

  return useCallback(
    async (resource: ImageResource, signal?: AbortSignal): Promise<Blob> => {
      switch (resource.kind) {
        case 'user-avatar':
          return getUserAvatar(resource.userId, signal);
        case 'current-user-avatar':
          return getCurrentUserAvatar(signal);
        case 'thumbnail':
          return downloadThumbnail(resource.documentId, signal);
        case 'support-name-picture': {
          const { data } = await downloadIndividualSupportNamePicture(resource.url, signal);
          return data as Blob;
        }
        case 'service-task-document':
          return downloadServiceTaskDocument(resource.taskId, resource.documentId, signal);
        case 'service-task-artwork':
          return downloadArtworkFileForTask(resource.taskId, resource.documentId, signal);
        case 'service-task-artwork-preview':
          return downloadTaskArtworkPreview(resource.taskId, resource.documentId, signal);
        case 'service-task-campaign-monitor':
          return downloadCampaignMonitorImage(resource.taskId, resource.documentId, signal);
        case 'service-task-artwork-match':
          return downloadArtworkMatchFile(resource.taskId, resource.documentId, signal);
        case 'service-task-layout-sketch':
          return downloadServiceTaskLayoutSketch(resource.taskId, resource.documentId, signal);
        case 'service-task-layout-sketch-original':
          return downloadServiceTaskLayoutSketchOriginal(
            resource.taskId,
            resource.documentId,
            signal,
          );
        case 'space-document':
          return downloadSpaceDocumentById(resource.spaceId, resource.documentId, signal);
        case 'space-thumbnail':
          return downloadSpaceThumbnail(resource.spaceId, resource.documentId, signal);
        case 'space-support-picture':
          return downloadSpaceSupportPicture(resource.spaceId, resource.documentId, signal);
        case 'space-layout-sketch':
          return downloadSpaceLayoutSketch(resource.spaceId, resource.documentId, signal);
        case 'location-document':
          return downloadLocationDocument(resource.locationId, resource.documentId, signal);
        case 'location-floor-map':
          return downloadFloorMaps(resource.locationId, resource.documentId, signal);
        case 'kiosk-picture':
          return downloadKioskPicture(resource.kioskId, resource.documentId, signal);
        case 'kiosk-rental-contract':
          return downloadRentalContract(
            resource.kioskId,
            resource.rentalId,
            resource.documentId,
            signal,
          );
        case 'kiosk-rental-authorization':
          return downloadRentalAuthorization(
            resource.kioskId,
            resource.rentalId,
            resource.documentId,
            signal,
          );
        case 'order-document':
          return downloadOrderDocumentById(resource.orderId, resource.documentId, signal);
        case 'order-layout-sketch-image':
          return downloadOrderDocumentImageById(resource.orderId, resource.documentId, signal);
        case 'order-artwork':
          return downloadArtworkDocumentForOrder(
            resource.orderId,
            resource.bookingId,
            resource.documentId,
            signal,
          );
        case 'order-artwork-preview':
          return downloadArtworkPreviewDocumentForOrder(
            resource.orderId,
            resource.bookingId,
            resource.documentId,
            signal,
          );
        case 'order-booking-artwork-match':
          return downloadBookingArtworkMatchFile(
            resource.orderId,
            resource.bookingId,
            resource.documentId,
            signal,
          );
        case 'order-booking-campaign-monitor':
          return downloadBookingCampaignMonitorFile(
            resource.orderId,
            resource.bookingId,
            resource.documentId,
            signal,
          );
        case 'mediaplan-artwork':
          return downloadArtworkDocumentForMediaPlan(
            resource.planId,
            resource.bookingId,
            resource.documentId,
            signal,
          );
        case 'mediaplan-artwork-preview':
          return downloadArtworkPreviewDocumentForMediaPlan(
            resource.planId,
            resource.bookingId,
            resource.documentId,
            signal,
          );
        case 'mediaplan-document':
          return downloadMediaPlanDocumentById(resource.planId, resource.documentId, signal);
        case 'mediaplan-booking-artwork-match':
          return downloadMediaPlanBookingArtworkMatchFile(
            resource.planId,
            resource.bookingId,
            resource.documentId,
            signal,
          );
        case 'advertising-package-document':
          return downloadAdvertisingPackageDocument(
            resource.packageId,
            resource.documentId,
            signal,
          );
        case 'advertising-package-support-picture':
          return downloadPackageSupportPicture(resource.packageId, resource.documentId, signal);
        case 'company-document':
          return downloadCompanyDocument(resource.companyId, resource.documentId, signal);
        case 'organization-document':
          return downloadOrganizationDocument(resource.organizationId, resource.documentId, signal);
      }
    },
    [
      downloadThumbnail,
      downloadServiceTaskDocument,
      downloadCampaignMonitorImage,
      downloadArtworkMatchFile,
      downloadServiceTaskLayoutSketch,
      downloadServiceTaskLayoutSketchOriginal,
      downloadArtworkFileForTask,
      downloadTaskArtworkPreview,
      downloadArtworkDocumentForOrder,
      downloadArtworkPreviewDocumentForOrder,
      downloadArtworkDocumentForMediaPlan,
      downloadArtworkPreviewDocumentForMediaPlan,
      downloadSpaceDocumentById,
      downloadSpaceThumbnail,
      downloadSpaceSupportPicture,
      downloadSpaceLayoutSketch,
      downloadIndividualSupportNamePicture,
      downloadOrderDocumentById,
      downloadOrderDocumentImageById,
      downloadBookingArtworkMatchFile,
      downloadBookingCampaignMonitorFile,
      downloadLocationDocument,
      downloadFloorMaps,
      downloadKioskPicture,
      downloadRentalContract,
      downloadRentalAuthorization,
      downloadAdvertisingPackageDocument,
      downloadPackageSupportPicture,
      downloadMediaPlanDocumentById,
      downloadMediaPlanBookingArtworkMatchFile,
      downloadCompanyDocument,
      downloadOrganizationDocument,
      getUserAvatar,
      getCurrentUserAvatar,
    ],
  );
};

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

export interface UseResourceImageOptions {
  size?: ImageSizeSpec;
  format?: ResizeFormat;
  fit?: ResizeFit;
  enabled?: boolean;
}

/**
 * Load + resize + cache a server-fetched image, identified by a typed
 * `ImageResource` descriptor. Returns `{ url, sourceUrl, isLoading, ... }`.
 *
 * For rendering, prefer `<ResourceImage>` or `<Avatar>` which wrap this hook.
 * Use this hook directly when you need the URL string (e.g. background-image
 * styles, lightbox prefetch).
 */
export const useResourceImage = (
  resource: ImageResource | null,
  options: UseResourceImageOptions = {},
): UseImageResult => {
  const dispatchFetch = useResourceFetcher();

  const fetcher = useCallback(
    (_id: string | number, signal?: AbortSignal) => dispatchFetch(resource!, signal),
    [dispatchFetch, resource],
  );

  const queryKeyPrefix = useMemo(
    () => (resource ? resourceQueryKeyPrefix(resource) : []),
    [resource],
  );

  return useCachedImage({
    objectId: resource ? resourceLeafId(resource) : null,
    size: options.size,
    fetcher,
    queryKeyPrefix,
    format: options.format,
    fit: options.fit,
    enabled: options.enabled,
  });
};

/**
 * Bare-function variant of `usePrefetchResource`: useful when you already
 * have a `QueryClient` and a dispatcher in scope (rare). Most consumers
 * should reach for `usePrefetchResource()` instead.
 */
export const prefetchResource = (
  queryClient: QueryClient,
  resource: ImageResource,
  dispatchFetch: (r: ImageResource, signal?: AbortSignal) => Promise<Blob>,
): Promise<Blob> =>
  queryClient.fetchQuery({
    queryKey: resourceQueryKey(resource),
    queryFn: ({ signal }) => dispatchFetch(resource, signal),
    staleTime: Infinity,
    cacheTime: CACHE_TIME_MS,
  });

/**
 * Hook form of `prefetchResource`. Wraps `useQueryClient` + the internal
 * fetcher so callers only need one hook to imperatively load a resource
 * (e.g. populating a lightbox or PDF viewer that needs URL strings in hand).
 */
export const usePrefetchResource = () => {
  const queryClient = useQueryClient();
  const dispatchFetch = useResourceFetcher();
  return useCallback(
    (resource: ImageResource) => prefetchResource(queryClient, resource, dispatchFetch),
    [queryClient, dispatchFetch],
  );
};
